import { GeckoFeatureModelType } from 'types/assetModel/assetModelEnums'
import {
  FeatureTableOptions,
  GeckoEditableFeature,
} from 'types/assetModel/features/baseClasses/geckoEditableFeature'
import {
  GeckoPolygonalPlate,
  GeckoPolygonalPlateJSON,
  parseCoordinatesFromString,
} from 'types/assetModel/features/geckoPolygonalPlate'
import {
  ExternalUserFieldAccess,
  FormField,
  getFormFieldByDataKey,
  InputType,
  UnitMagnitude,
} from 'types/geckoFormTypesAndHelpers'
import {
  requiredCoordinatesArraySchema,
  requiredStringSchema,
} from 'utils/zodHelpers'

export interface GeckoShipDeckLockerJSON {
  type: GeckoFeatureModelType.ShipDeckLocker
  name: string
  feature_geometry: {
    coordinates: { x: number; y: number }[]
  }
}

export class GeckoShipDeckLocker extends GeckoEditableFeature {
  readonly name: string
  readonly type = GeckoFeatureModelType.ShipDeckLocker
  readonly polygonalPlate: GeckoPolygonalPlate

  /**
   * Private constructor, allowing for a factory method that can return undefined
   * @param id
   * @param name
   * @param polygonalPlate
   */

  private constructor(
    id: string,
    name: string,
    polygonalPlate: GeckoPolygonalPlate
  ) {
    super(id)
    this.name = name
    this.polygonalPlate = polygonalPlate
    if (this.polygonalPlate.geometry.length < 4) {
      throw new Error('Locker must have at least 4 points in its geometry')
    }
  }

  public static CreateFromJSON(
    componentID: string,
    lockerID: string,
    lockerJSON: GeckoShipDeckLockerJSON
  ): GeckoShipDeckLocker {
    const lockerName = lockerJSON.name as string

    const polygonalPlateJSON: GeckoPolygonalPlateJSON = {
      type: GeckoFeatureModelType.PolygonalPlate,
      name: `${lockerName} Plate`,
      feature_geometry: lockerJSON.feature_geometry,
      installation_date: undefined,
    }

    const polygonalPlate = GeckoPolygonalPlate.CreateFromJSON(
      componentID,
      `${lockerID}_plate`,
      polygonalPlateJSON
    )

    return new GeckoShipDeckLocker(lockerID, lockerName, polygonalPlate)
  }

  public static CreateMapFromJSON(
    componentID: string,
    recordsJSON: Record<string, GeckoShipDeckLockerJSON>
  ): Map<string, GeckoShipDeckLocker> {
    return Object.keys(recordsJSON).reduce((resultObj, id) => {
      resultObj.set(
        id,
        GeckoShipDeckLocker.CreateFromJSON(componentID, id, recordsJSON[id])
      )
      return resultObj
    }, new Map<string, GeckoShipDeckLocker>())
  }

  asJSON() {
    return generateLocker(this.name, this.polygonalPlate)
  }
}

export function generateLocker(
  name: string,
  polygonalPlate: GeckoPolygonalPlate
): GeckoShipDeckLockerJSON {
  const plateJSON = polygonalPlate.asJSON()
  return {
    type: GeckoFeatureModelType.ShipDeckLocker,
    name: name,
    feature_geometry: plateJSON.feature_geometry,
  }
}

export const NewGeckoShipDeckLockerJSON: GeckoShipDeckLockerJSON = {
  type: GeckoFeatureModelType.ShipDeckLocker,
  name: 'New Ship Deck Locker',
  feature_geometry: {
    coordinates: [
      { x: 0, y: 0 },
      { x: 0, y: 0 },
      { x: 0, y: 0 },
      { x: 0, y: 0 },
    ],
  },
}

export const ShipDeckLockerFormFields = (
  lockerJSON: GeckoShipDeckLockerJSON
) => {
  return [
    {
      dataKey: 'name',
      label: 'Locker Name',
      value: lockerJSON.name,
      unit_magnitude: null,
      initialValue: 'Locker',
      validationRule: requiredStringSchema,
      externalUserAccess: ExternalUserFieldAccess.ReadOnly,
      isNeededForDerivedLayer: false,
      inputType: InputType.Text,
    },
    {
      dataKey: 'coordinates',
      label: 'Coordinates',
      value: lockerJSON.feature_geometry.coordinates
        .map((coordinate) => `[${coordinate.x}, ${coordinate.y}]`)
        .join(', '),
      unit_magnitude: UnitMagnitude.FeetOrMeters,
      validationRule: requiredCoordinatesArraySchema(4),
      externalUserAccess: ExternalUserFieldAccess.ReadOnly,
      isNeededForDerivedLayer: false,
      inputType: InputType.Coordinates,
    },
  ]
}

export const ShipDeckLockerJSONFromFormFields = (formFields: FormField[]) => {
  const coordinates = getFormFieldByDataKey(formFields, 'coordinates')
    ?.value as string

  const polygonalPlateJSON: GeckoPolygonalPlateJSON = {
    type: GeckoFeatureModelType.PolygonalPlate,
    name: `${getFormFieldByDataKey(formFields, 'name')?.value as string} Plate`,
    feature_geometry: { coordinates: parseCoordinatesFromString(coordinates) },
    installation_date: undefined,
  }

  const polygonalPlate = GeckoPolygonalPlate.CreateFromJSON(
    polygonalPlateJSON.name,
    polygonalPlateJSON.type,
    polygonalPlateJSON
  )

  return generateLocker(
    getFormFieldByDataKey(formFields, 'name')?.value as string,
    polygonalPlate
  )
}

export const ShipDeckLockerFeatureTableOptions: FeatureTableOptions = {
  type: GeckoFeatureModelType.ShipDeckLocker,
  title: 'Lockers',
  singularName: 'Ship Deck Locker',
  fields: ['name', 'coordinates'],
  tableMessage:
    'To see how to enter coordinates to render a locker correctly, please see the <a href="https://geckorobotics.atlassian.net/wiki/spaces/SE/pages/4595744769/Polygonal+Plates" target="_blank">documentation</a> on polygonal plates.',
}
